/**
 * Carregamento do índice e revalidação — o mesmo ciclo do rastreio de nuvens.
 *
 * Só baixa com o rastreio da chuva ligado. Depois, revalida a cada ciclo da
 * atualização automática e quando a janela do loop anda. O índice tem poucos
 * KB e a máquina responde 304 quando nada mudou.
 */
import { createElement, useEffect } from 'react';
import { subscribeSelector } from '../lib/store';
import { useMapInstance } from '../map/mapContext';
import { useTimeStore } from '../state/time';
import { BASE_RASTREIO_CHUVA, carregarIndice, type IndiceChuva } from './api';
import { FichaDaChuva } from './FichaDaChuva';
import { RastreioChuvaLayer } from './layer';
import { useRastreioChuvaStore } from './store';

/** Revalidações a menos disto do último pedido são ignoradas. */
const INTERVALO_MIN_MS = 55_000;

let emVoo: AbortController | null = null;
let jaCarregou = false;
let ultimoPedido = 0;
let camada: RastreioChuvaLayer | null = null;

const ligado = () => useRastreioChuvaStore.getState().ligado;

function mesmoIndice(a: IndiceChuva | null, b: IndiceChuva): boolean {
  if (!a || a.quadros.length !== b.quadros.length) return false;
  return a.quadros.every((q, i) => q.c === b.quadros[i]!.c && q.n === b.quadros[i]!.n);
}

export async function carregarRastreioChuva(force = false): Promise<void> {
  if (!BASE_RASTREIO_CHUVA) return;
  const { situacao } = useRastreioChuvaStore.getState();
  if (!force && (situacao.estado === 'loading' || jaCarregou)) return;
  emVoo?.abort();
  const ctrl = new AbortController();
  emVoo = ctrl;
  ultimoPedido = Date.now();
  if (!jaCarregou) useRastreioChuvaStore.setState({ situacao: { estado: 'loading', erro: null } });
  try {
    const indice = await carregarIndice(ctrl.signal);
    if (ctrl.signal.aborted) return;
    jaCarregou = true;
    const atual = useRastreioChuvaStore.getState().indice;
    useRastreioChuvaStore.setState({
      indice: mesmoIndice(atual, indice) ? { ...atual!, gerado: indice.gerado } : indice,
      situacao: { estado: 'ready', erro: null },
    });
  } catch (e) {
    if (ctrl.signal.aborted) return;
    useRastreioChuvaStore.setState({ situacao: { estado: 'error', erro: e instanceof Error ? e.message : String(e) } });
  } finally {
    if (emVoo === ctrl) emVoo = null;
  }
}

/** Revalidação do ciclo automático — só com o rastreio da chuva ligado. */
export function refreshRastreioChuva(): void {
  if (!ligado()) return;
  camada?.tentarDeNovo();
  const nuncaCarregou = !jaCarregou && useRastreioChuvaStore.getState().situacao.estado === 'error';
  if (!jaCarregou && !nuncaCarregou) return;
  if (Date.now() - ultimoPedido < INTERVALO_MIN_MS) return;
  void carregarRastreioChuva(true);
}

function iniciarSync(): () => void {
  if (ligado()) void carregarRastreioChuva();
  const aoVoltarConexao = () => {
    ultimoPedido = 0;
    refreshRastreioChuva();
  };
  if (typeof window !== 'undefined') window.addEventListener('online', aoVoltarConexao);
  const unsubs = [
    useRastreioChuvaStore.subscribe((a, b) => {
      if (!a.ligado || b.ligado) return;
      if (!jaCarregou) void carregarRastreioChuva();
      else if (Date.now() - ultimoPedido >= INTERVALO_MIN_MS) void carregarRastreioChuva(true);
    }),
    subscribeSelector(useTimeStore, (s) => s.range?.end ?? null, refreshRastreioChuva),
  ];
  return () => {
    unsubs.forEach((u) => u());
    if (typeof window !== 'undefined') window.removeEventListener('online', aoVoltarConexao);
  };
}

/** Fim da hora mais nova rastreada (epoch ms), ou null. */
export function fimDoUltimo(indice: IndiceChuva | null): number | null {
  const q = indice?.quadros;
  return q?.length ? q[q.length - 1]!.fim : null;
}

/** Hook chamado UMA vez pelo app-core (`ui/modules.tsx`): o índice e a camada do mapa. */
export function useRastreioChuvaLayers(): void {
  const map = useMapInstance((s) => s.map);
  useEffect(() => (BASE_RASTREIO_CHUVA ? iniciarSync() : undefined), []);
  useEffect(() => {
    if (!map || !BASE_RASTREIO_CHUVA) return;
    const c = new RastreioChuvaLayer(map, (sistema) => createElement(FichaDaChuva, { sistema }));
    camada = c;
    return () => {
      if (camada === c) camada = null;
      c.dispose();
    };
  }, [map]);
}

/** Só para os testes. */
export function resetRastreioChuvaSync(): void {
  emVoo?.abort();
  emVoo = null;
  jaCarregou = false;
  ultimoPedido = 0;
}
